import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Search, FileText, Check, Clock, AlertCircle, 
  Globe, Copy, Eye, Download, RefreshCw, Plus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const LANGUAGE_LABELS = {
  en: 'English',
  es: 'Spanish',
  pt: 'Portuguese'
};

const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  pending_agreement: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  terminated: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const STATUS_LABELS = {
  active: 'Active',
  pending_agreement: 'Pending Agreement',
  inactive: 'Inactive',
  terminated: 'Terminated'
};

export const ContractorManagement = () => {
  const { api } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      const response = await api.get('/contractors/master-list');
      setContractors(response.data.contractors || []);
    } catch (err) {
      console.error('Error fetching contractors:', err);
      toast.error('Failed to load contractor list');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (contractorId) => {
    setLoadingDocs(true);
    try {
      const response = await api.get(`/contractors/documents/${contractorId}`);
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const filteredContractors = contractors.filter(c => {
    const matchesSearch = 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.contractor_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: contractors.length,
    active: contractors.filter(c => c.status === 'active').length,
    pending: contractors.filter(c => c.status === 'pending_agreement').length,
    signed: contractors.filter(c => c.agreement_signed).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-serif text-[#0A0A0A]">Contractor Management</h1>
          <p className="text-gray-500 text-sm mt-1">Manage contractor IDs and view signed agreements</p>
        </div>
        <button
          onClick={fetchContractors}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-colors text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C6A87C]/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#C6A87C]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A0A0A]">{stats.total}</p>
              <p className="text-xs text-gray-500">Total Contractors</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
              <Check className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A0A0A]">{stats.active}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A0A0A]">{stats.pending}</p>
              <p className="text-xs text-gray-500">Pending Agreement</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A0A0A]">{stats.signed}</p>
              <p className="text-xs text-gray-500">Agreements Signed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, ID, or email..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending_agreement">Pending Agreement</option>
          <option value="inactive">Inactive</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>

      {/* Contractor List */}
      <div className="bg-white border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Contractor ID
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                  Contact
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                  Language
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Agreement
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredContractors.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No contractors found
                  </td>
                </tr>
              ) : (
                filteredContractors.map((contractor) => (
                  <tr key={contractor.contractor_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[#C6A87C] font-medium">
                          {contractor.contractor_id}
                        </span>
                        <button
                          onClick={() => copyToClipboard(contractor.contractor_id)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copy ID"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#0A0A0A] font-medium">{contractor.name}</span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="text-sm">
                        <p className="text-gray-600">{contractor.email || '-'}</p>
                        <p className="text-gray-400 text-xs">{contractor.phone || ''}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 text-xs border ${STATUS_COLORS[contractor.status] || STATUS_COLORS.inactive}`}>
                        {STATUS_LABELS[contractor.status] || contractor.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        <Globe className="w-3 h-3" />
                        {LANGUAGE_LABELS[contractor.preferred_language] || 'English'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {contractor.agreement_signed ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <Check className="w-4 h-4" />
                          Signed
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-yellow-600 text-sm">
                          <Clock className="w-4 h-4" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => {
                          setSelectedContractor(contractor);
                          fetchDocuments(contractor.contractor_id);
                        }}
                        className="text-[#C6A87C] hover:text-[#B09265] text-sm flex items-center gap-1 ml-auto"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Document Modal */}
      {selectedContractor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-serif text-[#0A0A0A]">{selectedContractor.name}</h2>
                  <p className="text-[#C6A87C] font-mono">{selectedContractor.contractor_id}</p>
                </div>
                <button
                  onClick={() => setSelectedContractor(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {/* Contractor Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Email</p>
                  <p className="text-[#0A0A0A]">{selectedContractor.email || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Phone</p>
                  <p className="text-[#0A0A0A]">{selectedContractor.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Status</p>
                  <span className={`inline-flex items-center px-2 py-1 text-xs border ${STATUS_COLORS[selectedContractor.status]}`}>
                    {STATUS_LABELS[selectedContractor.status]}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Preferred Language</p>
                  <p className="text-[#0A0A0A]">{LANGUAGE_LABELS[selectedContractor.preferred_language]}</p>
                </div>
              </div>

              {/* Documents */}
              <h3 className="font-medium text-[#0A0A0A] mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Signed Documents
              </h3>
              
              {loadingDocs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : documents.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 p-6 text-center">
                  <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No documents found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="bg-gray-50 border border-gray-200 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-[#0A0A0A]">Independent Contractor Agreement</p>
                          <p className="text-sm text-gray-500 mt-1">
                            Signed: {new Date(doc.signed_at).toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-500">
                            Language: {LANGUAGE_LABELS[doc.language]}
                          </p>
                          <p className="text-sm text-gray-400 italic mt-1">
                            Signature: {doc.electronic_signature}
                          </p>
                        </div>
                        <span className="bg-green-500/20 text-green-600 text-xs px-2 py-1 border border-green-500/30">
                          {doc.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setSelectedContractor(null)}
                className="w-full py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ContractorManagement;
